// /stores/log.js
import { defineStore } from "pinia";
import logService from "@/api/logService";

export const useLogStore = defineStore("log", {
  state: () => ({
    logs: [],
    totalLogs: 0,
    isLoading: false,
    threadMessages: [],
    page: 1,
    size: 10,
    stakeholders: [],
    totalThreadMessages: 0,
    newActivityProjects: [], // New state for projects with new activity
    updatedThreadIds: [],
    threads: [],
    totalThreads: 0,
  }),
  actions: {
    // Fetch logs for a project with pagination
    async fetchLogsByProjectId(projectId, page = 1, size = 10, order = "asc") {
      this.isLoading = true;
      try {
        const { data } = await logService.getLogsByProjectId(
          projectId,
          order,
          page,
          size
        );
        const { total, items } = data;

        this.totalLogs = total;
        this.logs = items;
        this.currentPage = page;
        this.stakeholders = data.stakeholders;

        this.hasMoreLogs = this.logs.length < this.total;
      } catch (error) {
        console.error("Error fetching logs:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchThreadsByProjectId(
      projectId,
      type = "all",
      order = "desc",
      page = 1,
      size = 10
    ) {
      this.isLoading = true;
      try {
        const { data } = await logService.getThreadsByProjectId(
          projectId,
          type,
          order,
          page,
          size
        );

        this.threads = data.threads;
        this.totalThreads = data.total;
        this.currentPage = page;
        this.hasMoreThreads = this.threads?.length < this.totalThreads;
      } catch (error) {
        console.error("Error fetching threads:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    // Search logs within a project
    async searchLogs(projectId, query, type = "all", page = 1, size = 10) {
      this.isLoading = true;
      try {
        const { data } = await logService.searchLogs(
          projectId,
          query,
          type,
          page,
          size
        );
        // console.log("Search results", data);
        this.logs = data.items;
        this.totalThreads = data.total;
        this.currentPage = page;
        this.hasMoreThreads = this.threads?.length < this.totalThreads;
      } catch (error) {
        console.error("Error searching logs:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    async searchThreads(projectId, query, type = "all", page = 1, size = 10) {
      this.isLoading = true;
      try {
        const { data } = await logService.searchThreads(
          projectId,
          query,
          type,
          page,
          size
        );
        this.threads = data.threads;
        this.totalThreads = data.total;
        this.currentPage = page;
        this.hasMoreThreads = this.threads?.length < this.totalThreads;
      } catch (error) {
        console.error("Error searching threads:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    // Fetch projects with new activity (unseen logs)
    async fetchProjectsWithNewActivity() {
      this.isLoading = true;
      try {
        const { data } = await logService.getProjectsWithNewActivity();
        this.newActivityProjects = data;
      } catch (error) {
        console.error("Error fetching projects with new activity:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    async deleteEmailLog(communicationId) {
      try {
        await logService.deleteEmailLog(communicationId);
        this.logs = this.logs.filter((log) => log.id !== communicationId);
      } catch (error) {
        console.error("Error deleting email log:", error);
        throw error;
      }
    },
    async deleteEmailThread(threadId) {
      try {
        await logService.deleteEmailThread(threadId);
        this.logs = this.logs.filter((log) => log.thread_id !== threadId);
      } catch (error) {
        console.error("Error deleting email thread:", error);
        throw error;
      }
    },
    async deleteTextLog(communicationId) {
      try {
        await logService.deleteTextLog(communicationId);
        this.logs = this.logs.filter((log) => log.id !== communicationId);
      } catch (error) {
        console.error("Error deleting text log:", error);
        throw error;
      }
    },
    async deleteTextThread(threadId) {
      try {
        await logService.deleteTextThread(threadId);
        this.logs = this.logs.filter((log) => log.thread_id !== threadId);
      } catch (error) {
        console.error("Error deleting text thread:", error);
        throw error;
      }
    },
    async fetchMessagesByThreadId(
      threadId,
      order = "asc",
      page = 1,
      size = 100
    ) {
      this.isLoading = true;
      try {
        const { data } = await logService.getMessagesByThreadId(
          threadId,
          order,
          page,
          size
        );
        this.threadMessages = data.items;
        this.stakeholders = data.stakeholders;
        this.totalThreadMessages = data.total;
      } catch (error) {
        console.error("Error fetching messages by thread ID:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    async fetchMailsByThreadId(threadId, order = "asc", page = 1, size = 100) {
      this.isLoading = true;
      try {
        const { data } = await logService.getMailsByThreadId(
          threadId,
          order,
          page,
          size
        );
        this.threadMessages = data.communications;
        this.stakeholders = data.stakeholders;
        this.totalThreadMessages = data.total;
      } catch (error) {
        console.error("Error fetching messages by thread ID:", error);
        throw error;
      } finally {
        this.isLoading = false;
      }
    },
    async sendTextMessage(formData) {
      try {
        const { data } = await logService.sendTextMessage(formData);
        return data;
      } catch (error) {
        console.error("Error sending text message:", error);
        throw error;
      }
    },
    async fetchNewLogsByProjectId(projectId) {
      try {
        const { data } = await logService.getNewLogsByProjectId(projectId);
        this.updatedThreadIds = data.new_thread_ids || [];
      } catch (error) {
        console.error("Error fetching new logs:", error);
      }
    },
    async findTextThread(projectId, stakeholders) {
      try {
        const { data } = await logService.findTextThread(
          projectId,
          stakeholders
        );
        return data.thread_id;
      } catch (error) {
        console.error("Error finding SMS thread:", error);
        throw error;
      }
    },
    clearData() {
      this.threads = [];
      this.totalThreads = 0;
      this.threadMessages = [];
      this.stakeholders = [];
      this.totalThreadMessages = 0;
      this.updatedThreadIds = [];
    },
  },
});
